// Written accompaniment instructions projected onto a concrete playback order.
// The caller owns form expansion, so repeats, D.S. returns and passage starts
// all use the same written cues without any persistent enter/leave state.

import { fractionToNumber, measureQuarters } from './duration';
import type { PerformanceMeasureOccurrence } from './partPlayback';
import type { BandCue, BandCueChannel, Score } from './types';

const EPSILON = 1e-9;
const CHANNELS: readonly BandCueChannel[] = ['comp', 'bass', 'drums'];

/** One accompaniment instruction for one actual visit to a written measure. */
export interface BandCueInterval {
  channel: BandCueChannel;
  startBeat: number;
  endBeat: number;
  mode: BandCue['mode'];
  /** In the playback beat domain, and never longer than this occurrence. */
  hitDurationBeats?: number;
  formMeasureIndex: number;
  formMeasureId: string;
  occurrenceIndex: number;
}

export interface BandCueWindow {
  startBeat: number;
  endBeat: number;
}

/** Timing shared by pitched notes and unpitched accompaniment events. */
export interface BandCueTimedNote {
  beat: number;
  durationBeats: number;
}

/**
 * Expand only explicit, semantic cues. An absent cue means ordinary playback
 * for that occurrence; prose, tune names and harmony (including N.C.) never
 * create instructions here. `play` remains an explicit interval so the player
 * can distinguish a written drum entrance from its usual N.C. handling.
 */
export function buildBandCuePlayback(
  score: Score,
  occurrences: readonly PerformanceMeasureOccurrence[],
): BandCueInterval[] {
  let meter: [number, number] = [4, 4];
  const capacities = score.form.map((measure) => {
    meter = measure.timeSig ?? meter;
    return measure.pickupBeats == null
      ? fractionToNumber(measureQuarters(meter))
      : (measure.pickupBeats * 4) / meter[1];
  });
  const intervals: BandCueInterval[] = [];

  occurrences.forEach((occurrence, occurrenceIndex) => {
    const measure = score.form[occurrence.formMeasureIndex];
    const cue = measure?.bandCue;
    if (
      !cue ||
      !Number.isFinite(occurrence.startBeat) ||
      !Number.isFinite(occurrence.beats) ||
      occurrence.beats <= EPSILON
    ) {
      return;
    }
    const channels = new Set(cue.channels ?? CHANNELS);
    let hitDurationBeats: number | undefined;
    if (cue.mode === 'hit') {
      const capacityQuarters = capacities[occurrence.formMeasureIndex];
      if (!Number.isFinite(capacityQuarters) || capacityQuarters <= EPSILON) return;
      const requested = cue.duration ? fractionToNumber(cue.duration) : 1;
      const durationQuarters = Number.isFinite(requested) && requested > 0 ? requested : 1;
      hitDurationBeats = Math.min(
        occurrence.beats,
        (durationQuarters * occurrence.beats) / capacityQuarters,
      );
    }
    // Canonical channel order and original occurrence indexes are stable even
    // when the supplied channel list is reordered or contains duplicates.
    for (const channel of CHANNELS) {
      if (!channels.has(channel)) continue;
      intervals.push({
        channel,
        startBeat: occurrence.startBeat,
        endBeat: occurrence.startBeat + occurrence.beats,
        mode: cue.mode,
        ...(hitDurationBeats !== undefined ? { hitDurationBeats } : {}),
        formMeasureIndex: occurrence.formMeasureIndex,
        formMeasureId: measure.id,
        occurrenceIndex,
      });
    }
  });

  return intervals;
}

/** Half-open bounds: a cue ends exactly when the following measure begins. */
export function bandCueAtBeat(
  intervals: readonly BandCueInterval[],
  channel: BandCueChannel,
  beat: number,
): BandCueInterval | undefined {
  return intervals.find(
    (interval) =>
      interval.channel === channel &&
      beat >= interval.startBeat - EPSILON &&
      beat < interval.endBeat - EPSILON,
  );
}

function mergeWindows(windows: BandCueWindow[]): BandCueWindow[] {
  windows.sort((a, b) => a.startBeat - b.startBeat || a.endBeat - b.endBeat);
  const merged: BandCueWindow[] = [];
  for (const window of windows) {
    if (window.endBeat <= window.startBeat + EPSILON) continue;
    const previous = merged[merged.length - 1];
    if (previous && window.startBeat <= previous.endBeat + EPSILON) {
      previous.endBeat = Math.max(previous.endBeat, window.endBeat);
    } else {
      merged.push({ ...window });
    }
  }
  return merged;
}

/**
 * Silence after an explicit hit, or throughout a rest. These windows can gate
 * continuous audio (such as drum loops) after the separate downbeat attack.
 */
export function bandCueSilenceWindows(
  intervals: readonly BandCueInterval[],
  channel: BandCueChannel,
): BandCueWindow[] {
  return mergeWindows(
    intervals
      .filter((interval) => interval.channel === channel && interval.mode !== 'play')
      .map((interval) => ({
        startBeat:
          interval.startBeat + (interval.mode === 'hit' ? (interval.hitDurationBeats ?? 0) : 0),
        endBeat: interval.endBeat,
      })),
  );
}

/**
 * Remove ordinary style/authored attacks from rest and hit occurrences, and
 * stop any preceding sustain exactly at their boundary. The caller inserts
 * the explicit downbeat hit separately, using the harmony and hit duration;
 * retaining ordinary attacks within its first beat would add unwanted notes.
 * `play` leaves the existing track unchanged. No synthetic resumed attacks
 * are created after a rest, and input notes/intervals are never mutated.
 */
export function filterBandCueNotes<T extends BandCueTimedNote>(
  notes: readonly T[],
  intervals: readonly BandCueInterval[],
  channel: BandCueChannel,
): T[] {
  const suppressed = mergeWindows(
    intervals
      .filter((interval) => interval.channel === channel && interval.mode !== 'play')
      .map(({ startBeat, endBeat }) => ({ startBeat, endBeat })),
  );
  if (suppressed.length === 0) return [...notes];

  const filtered: T[] = [];
  for (const note of notes) {
    if (
      suppressed.some(
        (window) => note.beat >= window.startBeat - EPSILON && note.beat < window.endBeat - EPSILON,
      )
    ) {
      continue;
    }
    const endBeat = note.beat + Math.max(0, note.durationBeats);
    const boundary = suppressed.find(
      (window) => window.startBeat > note.beat + EPSILON && window.startBeat < endBeat - EPSILON,
    );
    filtered.push(boundary ? { ...note, durationBeats: boundary.startBeat - note.beat } : note);
  }
  return filtered;
}
